import { AdPlacement } from "@/components/ads/AdPlacement";
import {
  SciBreadcrumb,
  SciExplainer,
  SciPage,
  type SolveConfig,
  SolveForCalc,
} from "@/lib/science/sci-ui";
import { m } from "@/paraglide/messages";
import { solveMassMolarity } from "../lib/mass-molarity";

const MASS_UNITS = [
  { value: "g", label: "g" },
  { value: "mg", label: "mg" },
  { value: "µg", label: "µg" },
  { value: "ng", label: "ng" },
  { value: "kg", label: "kg" },
];
const CONC_UNITS = [
  { value: "M", label: "M" },
  { value: "mM", label: "mM" },
  { value: "µM", label: "µM" },
  { value: "nM", label: "nM" },
];
const VOL_UNITS = [
  { value: "L", label: "L" },
  { value: "mL", label: "mL" },
  { value: "µL", label: "µL" },
];

const config: SolveConfig = {
  resultTestId: "massmol-result",
  hintLeaveBlank: () => m.massmol_hint_leave_blank(),
  hintInvalid: () => m.massmol_hint_invalid(),
  resultPrefix: () => m.sci_result_solves(),
  fields: [
    {
      id: "mass",
      label: () => m.massmol_field_mass(),
      kind: "mass",
      units: MASS_UNITS,
    },
    {
      id: "conc",
      label: () => m.massmol_field_conc(),
      kind: "conc",
      units: CONC_UNITS,
      placeholder: "1",
    },
    {
      id: "volume",
      label: () => m.massmol_field_volume(),
      kind: "volume",
      units: VOL_UNITS,
      placeholder: "1",
    },
    {
      id: "mw",
      label: () => m.massmol_field_mw(),
      kind: "plain",
      placeholder: "58.44",
    },
  ],
  solve: (base) => {
    const blanks = ["mass", "conc", "volume", "mw"].filter(
      (id) => base[id] === null,
    );
    if (blanks.length !== 1) return null;
    const value = solveMassMolarity({
      mass: base.mass,
      conc: base.conc,
      volume: base.volume,
      mw: base.mw,
    });
    return value === null ? null : { id: blanks[0], value };
  },
};

export function CalculatorPage() {
  return (
    <SciPage>
      <SciBreadcrumb
        homeLabel={m.sci_breadcrumb_home()}
        toolName={m.massmol_tool_name()}
        toolPath="/mass-molarity"
      />

      <header className="mt-6 mb-8">
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
          {m.massmol_h1()}
        </h1>
        <p className="mt-3 leading-relaxed text-muted-foreground">
          {m.massmol_intro()}
        </p>
      </header>

      <SolveForCalc config={config} />

      <SciExplainer
        howToTitle={m.sci_howto_title()}
        howTo={m.massmol_howto()}
        examplesTitle={m.sci_examples_title()}
        examples={[m.massmol_ex1(), m.massmol_ex2(), m.massmol_ex3()]}
        faqTitle={m.sci_faq_title()}
        faqs={[
          { q: m.massmol_faq_q1(), a: m.massmol_faq_a1() },
          { q: m.massmol_faq_q2(), a: m.massmol_faq_a2() },
          { q: m.massmol_faq_q3(), a: m.massmol_faq_a3() },
          { q: m.massmol_faq_q4(), a: m.massmol_faq_a4() },
        ]}
      />

      <AdPlacement className="mt-10" />
    </SciPage>
  );
}
